import React, { useState } from "react";
import { endpoints } from "@/lib/api";
import { UserPlus, UserCheck } from "lucide-react";
import { toast } from "sonner";

// Small toggleable follow button used on win cards and community posts.
// `following` is a controlled boolean; onChange(newState) is called after the server confirms.
export default function FollowButton({ author, following, onChange, size = "sm", testid, selfAuthor }) {
  const [busy, setBusy] = useState(false);
  const [optimistic, setOptimistic] = useState(following);

  React.useEffect(() => { setOptimistic(following); }, [following]);

  const toggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    const next = !optimistic;
    setOptimistic(next);
    try {
      const { data } = await endpoints.toggleFollow(author);
      setOptimistic(data.following);
      onChange && onChange(data.following);
      toast[data.following ? "success" : "message"](data.following ? `Following ${author}.` : `Unfollowed ${author}.`);
    } catch {
      setOptimistic(!next);
      toast.error("Could not update.");
    }
    setBusy(false);
  };

  const isSelf = !author || (selfAuthor && author === selfAuthor);
  if (isSelf) return null;

  const on = optimistic;
  const pad = size === "sm" ? "px-2.5 py-1 text-xs" : "px-3 py-1.5 text-sm";
  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      data-testid={testid || `follow-${author}`}
      aria-pressed={on}
      className={`inline-flex items-center gap-1.5 rounded-full border hairline tab-link ${pad} ${
        on
          ? "bg-[hsl(var(--moss))] text-[hsl(var(--paper))] border-transparent"
          : "hover:bg-[hsl(var(--sand))] text-[hsl(var(--fg))]"
      } ${busy ? "opacity-60" : ""}`}
    >
      {on ? <UserCheck size={12} /> : <UserPlus size={12} />}
      {on ? "Following" : "Follow"}
    </button>
  );
}
